import React, { useMemo, useState } from 'react';
import { logUsage } from '../../../../services/eventLog';
import type { Column } from '../../utils/transformLoad';
import SuggestionPanel from '../../mapping/SuggestionPanel';
import { useTranslation } from 'react-i18next';

export interface MappingItem { target: string; source: string; expr: string }

interface Props {
  sourceColumns: Column[];
  targetColumns: Column[];
  initial: MappingItem[];
  /** 소스 샘플 행 — 매핑 제안이 값 패턴(날짜 형식·길이 초과)을 판정하는 데 쓴다 */
  sourceRows?: Array<Record<string, unknown>>;
  onSave: (mapping: MappingItem[]) => void;
  onClose: () => void;
}

const FUNCS = 'upper(v) lower(v) trim(v) substr(v, start, len) concat(a, b, …) replace(v, a, b) lpad/rpad(v, n, ch) length(v) nvl(v, d) coalesce(…) to_int(v) to_num(v) to_str(v) round(x, n) iif(cond, a, b) today() now()';

/**
 * 항목 매핑 및 변환 정의 팝업 — 타깃 항목마다 (소스 항목 | 변환식) 을 지정한다.
 *
 * 변환식은 **DB 가 아니라 OpenFlow 가 행마다 계산한다**(백엔드 task_functions._eval_expr — 허용한 함수만 쓰는
 * 샌드박스). 그래서 NVL·TO_CHAR 같은 DB 함수는 쓸 수 없다. DB 기능이 필요하면 소스 추출을 SQL 쿼리로 바꿔
 * SELECT 에서 계산한다 — 화면에 그렇게 적는다(현장 요청은 'DBMS 쿼리 로직을 쓰라고 명시' 였지만, 그렇게 쓰면
 * 실행이 실패하므로 실제 동작대로 안내한다).
 *
 * 테이블 컬럼이 바뀐 뒤 열면 매핑이 가리키는 항목 중 없어진 것을 표시하고, 새로 생긴 타깃 항목을 추가하거나
 * 없어진 행을 지우는 버튼을 준다.
 * 변환식은 소스 항목명을 변수로 쓰는 식: amount * 1.1, upper(customer), substr(order_dt, 1, 7), iif(status == 'A', '승인', '기타')
 * 식이 비어 있으면 소스 항목값을 그대로 옮긴다(Move).
 */
const TransformMappingModal: React.FC<Props> = ({ sourceColumns, targetColumns, initial, sourceRows, onSave, onClose }) => {
  const { t } = useTranslation();
  const [rows, setRows] = useState<MappingItem[]>(initial.length ? initial.map((m) => ({ target: m.target || '', source: m.source || '', expr: m.expr || '' })) : []);
  const srcNames = useMemo(() => sourceColumns.map((c) => c.name), [sourceColumns]);
  const tgtNames = useMemo(() => targetColumns.map((c) => c.name), [targetColumns]);

  const update = (i: number, patch: Partial<MappingItem>) => setRows((prev) => prev.map((r, k) => (k === i ? { ...r, ...patch } : r)));
  const remove = (i: number) => setRows((prev) => prev.filter((_, k) => k !== i));
  const add = () => setRows((prev) => [...prev, { target: '', source: '', expr: '' }]);
  const move = (i: number, d: -1 | 1) => setRows((prev) => {
    const j = i + d; if (j < 0 || j >= prev.length) return prev;
    const next = [...prev]; [next[i], next[j]] = [next[j], next[i]]; return next;
  });
  /** 타깃 항목(없으면 소스 항목)으로 행을 채우고, 같은 이름의 소스 항목을 자동 연결 */
  const autoFill = () => {
    const base = tgtNames.length ? tgtNames : srcNames;
    setRows(base.map((t) => {
      const existing = rows.find((r) => r.target === t);
      return existing || { target: t, source: srcNames.includes(t) ? t : '', expr: '' };
    }));
  };

  const upperIn = (names: string[]) => { const set = new Set(names.map((x) => x.toUpperCase())); return (v: string) => set.has((v || '').toUpperCase()); };
  const inTarget = upperIn(tgtNames);
  const inSource = upperIn(srcNames);
  // 컬럼 정보가 있을 때만 판정한다(없으면 모른다)
  const goneTarget = (r: MappingItem) => tgtNames.length > 0 && !!r.target && !inTarget(r.target);
  const goneSource = (r: MappingItem) => srcNames.length > 0 && !!r.source && !r.expr.trim() && !inSource(r.source);
  const unmappedTargets = tgtNames.filter((tn) => !rows.some((r) => (r.target || '').toUpperCase() === tn.toUpperCase()));
  const goneCount = rows.filter((r) => goneTarget(r) || goneSource(r)).length;
  const removeGone = () => setRows((prev) => prev.filter((r) => !goneTarget(r) && !goneSource(r)));
  const addUnmapped = () => setRows((prev) => [...prev, ...unmappedTargets.map((tn) => ({ target: tn, source: srcNames.find((x) => x.toUpperCase() === tn.toUpperCase()) || '', expr: '' }))]);

  // 문제는 코드로 가르고 표시할 때 번역한다 — 문구로 비교하면 언어를 바꿀 때 판정이 깨진다
  type Problem = '' | 'noTarget' | 'needSource' | 'goneTarget' | 'goneSource';
  const PROBLEM_KEY: Record<Exclude<Problem, ''>, string> = {
    noTarget: 'property:tm.problemNoTarget', needSource: 'property:tm.problemNeedSource',
    goneTarget: 'property:tm.problemGoneTarget', goneSource: 'property:tm.problemGoneSource',
  };
  const problems: Problem[] = rows.map((r) => (!r.target ? 'noTarget'
    : (!r.source && !r.expr ? 'needSource'
      : goneTarget(r) ? 'goneTarget' : goneSource(r) ? 'goneSource' : '')));
  const problemText = (p: Problem) => (p ? t(PROBLEM_KEY[p]) : '');
  const dupTargets = new Set(rows.map((r) => r.target).filter((tn, i, a) => tn && a.indexOf(tn) !== i));
  // 없어진 항목은 저장을 막지 않는다(곧 컬럼이 추가될 수도 있다) — 대신 점검이 오류로 알린다
  const blocking = problems.filter((p) => p && p !== 'goneTarget' && p !== 'goneSource');
  const canSave = rows.length > 0 && blocking.length === 0 && dupTargets.size === 0;
  const [suggestOpen, setSuggestOpen] = useState(false);

  /** 제안 적용 — 같은 타깃이 이미 있으면 덮어쓰고, 없으면 추가한다 */
  const applySuggestions = (items: MappingItem[]) => {
    setRows((prev) => {
      const next = [...prev];
      items.forEach((it) => {
        const i = next.findIndex((r) => r.target === it.target);
        if (i >= 0) next[i] = { ...next[i], source: it.source, expr: it.expr };
        else next.push(it);
      });
      return next;
    });
    setSuggestOpen(false);
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-30" onMouseDown={onClose}>
      <div className="bg-white rounded shadow-lg w-[820px] max-w-[95vw] max-h-[85vh] flex flex-col" onMouseDown={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <div>
            <div className="text-sm font-semibold text-gray-800">{t('property:tm.title')}</div>
            <div className="text-xs text-gray-500">{t('property:tm.subtitle', { src: srcNames.length, tgt: tgtNames.length ? t('property:tm.targetCount', { n: tgtNames.length }) : t('property:tm.targetUnknown') })}</div>
          </div>
          <div className="flex items-center gap-1.5">
            <button className="px-2 py-1 text-xs rounded border border-blue-300 bg-blue-50 hover:bg-blue-100 text-blue-700"
              onClick={() => { logUsage('mapping.suggest', { detail: { source: srcNames.length, target: tgtNames.length } }); setSuggestOpen(true); }}
              disabled={!srcNames.length || !tgtNames.length}
              title={t('property:tm.suggestTitle')}>
              {t('property:tm.suggest')}
            </button>
            <button className="px-2 py-1 text-xs rounded border border-gray-300 hover:bg-gray-100 text-gray-700" onClick={autoFill} title={t('property:tm.autoFillTitle')}>
              {tgtNames.length ? t('property:tm.fillFromTarget') : t('property:tm.fillFromSource')}
            </button>
            <button className="px-2 py-1 text-xs rounded border border-gray-300 hover:bg-gray-100 text-gray-700" onClick={add}>{t('property:tm.addRow')}</button>
            <button className="px-2 py-1 text-xs rounded hover:bg-gray-100 text-gray-600" onClick={onClose} title={t('property:tm.close')}>✕</button>
          </div>
        </div>

        <div className="px-4 py-2 border-b border-gray-200 bg-blue-50 text-[0.6875rem] text-blue-900 leading-4">
          <b>{t('property:tm.noteEngine')}</b> {t('property:tm.noteNoDb')} <b>{t('property:tm.noteQueryMode')}</b>{t('property:tm.noteTail')}
        </div>
        {(goneCount > 0 || (unmappedTargets.length > 0 && rows.length > 0)) && (
          <div className="px-4 py-2 border-b border-amber-200 bg-amber-50 text-xs text-amber-900 flex items-center gap-2 flex-wrap">
            {goneCount > 0 && <span>{t('property:tm.goneRows', { n: goneCount })}</span>}
            {goneCount > 0 && <button className="px-2 py-0.5 rounded border border-amber-300 bg-white hover:bg-amber-100" onClick={removeGone}>{t('property:tm.removeGone')}</button>}
            {unmappedTargets.length > 0 && rows.length > 0 && <span>{t('property:tm.unmapped', { n: unmappedTargets.length })}</span>}
            {unmappedTargets.length > 0 && rows.length > 0 && <button className="px-2 py-0.5 rounded border border-amber-300 bg-white hover:bg-amber-100" onClick={addUnmapped} title={unmappedTargets.join(', ')}>{t('property:tm.addUnmapped')}</button>}
          </div>
        )}
        <div className="overflow-auto p-3 flex-1">
          {rows.length === 0 ? (
            <div className="text-sm text-gray-500 py-8 text-center">{t('property:tm.empty')}</div>
          ) : (
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-gray-500 border-b border-gray-200">
                  <th className="py-1.5 px-1 w-6">#</th>
                  <th className="py-1.5 px-1 w-[180px]">{t('property:tm.colTarget')}</th>
                  <th className="py-1.5 px-1 w-[170px]">{t('property:tm.colSource')}</th>
                  <th className="py-1.5 px-1">{t('property:tm.colExpr')}</th>
                  <th className="py-1.5 px-1 w-[104px]"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i} className={`border-b border-gray-100 ${problems[i] || dupTargets.has(r.target) ? 'bg-red-50' : ''}`} title={problemText(problems[i]) || undefined}>
                    <td className="py-1 px-1 text-gray-400">{i + 1}</td>
                    <td className="py-1 px-1">
                      {tgtNames.length ? (
                        <select className="w-full border border-gray-300 rounded px-1 py-0.5 bg-white text-xs" value={r.target} onChange={(e) => update(i, { target: e.target.value })}>
                          <option value="">{t('property:tm.choose')}</option>
                          {tgtNames.map((n) => <option key={n} value={n}>{n}</option>)}
                          {r.target && !tgtNames.includes(r.target) && <option value={r.target}>{r.target}</option>}
                        </select>
                      ) : (
                        <input className="w-full border border-gray-300 rounded px-1 py-0.5 bg-white text-xs" value={r.target} placeholder={t('property:tm.targetPlaceholder')} onChange={(e) => update(i, { target: e.target.value })} />
                      )}
                    </td>
                    <td className="py-1 px-1">
                      <select className="w-full border border-gray-300 rounded px-1 py-0.5 bg-white text-xs" value={r.source} onChange={(e) => update(i, { source: e.target.value })}>
                        <option value="">{t('property:tm.noSource')}</option>
                        {srcNames.map((n) => <option key={n} value={n}>{n}</option>)}
                        {r.source && !srcNames.includes(r.source) && <option value={r.source}>{r.source}</option>}
                      </select>
                    </td>
                    <td className="py-1 px-1">
                      <input className="w-full border border-gray-300 rounded px-1 py-0.5 bg-white font-mono text-xs" value={r.expr}
                        placeholder={t('property:tm.exprExample', { example: r.source ? `upper(${r.source})` : 'today()' })} onChange={(e) => update(i, { expr: e.target.value })} />
                    </td>
                    <td className="py-1 px-1 whitespace-nowrap">
                      <div className="flex items-center gap-1">
                        <button className="w-7 h-7 inline-flex items-center justify-center rounded border border-gray-200 text-sm text-gray-600 hover:bg-gray-100 disabled:opacity-30" disabled={i === 0} onClick={() => move(i, -1)} title={t('property:tm.up')}>↑</button>
                        <button className="w-7 h-7 inline-flex items-center justify-center rounded border border-gray-200 text-sm text-gray-600 hover:bg-gray-100 disabled:opacity-30" disabled={i === rows.length - 1} onClick={() => move(i, 1)} title={t('property:tm.down')}>↓</button>
                        <button className="w-7 h-7 inline-flex items-center justify-center rounded border border-gray-200 text-sm text-gray-500 hover:bg-red-50 hover:text-red-600" onClick={() => remove(i)} title={t('property:tm.remove')}>✕</button>
                      </div>
                      {(problems[i] === 'goneTarget' || problems[i] === 'goneSource') && (
                        <div className="text-[0.625rem] text-red-600 mt-0.5">{problemText(problems[i])}</div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="px-4 py-2 border-t border-gray-200 text-[0.6875rem] text-gray-500 leading-4">
          <div><b>{t('property:tm.exprLabel')}</b>{t('property:tm.exprHelp')} <code>amount * 1.1</code>, <code>upper(customer)</code>, <code>substr(order_dt, 1, 7)</code>, <code>{t('property:tm.iifExample')}</code></div>
          <div className="truncate" title={FUNCS}><b>{t('property:tm.funcsLabel')}</b>: {FUNCS}</div>
          {dupTargets.size > 0 && <div className="text-red-500">{t('property:tm.dupTargets', { cols: Array.from(dupTargets).join(', ') })}</div>}
        </div>
        <div className="px-4 py-2 border-t border-gray-200 flex justify-end gap-2">
          <button className="px-3 py-1 text-xs rounded bg-gray-200 hover:bg-gray-300 text-gray-700" onClick={onClose}>{t('property:tm.cancel')}</button>
          <button className="px-3 py-1 text-xs rounded bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50" disabled={!canSave} onClick={() => onSave(rows)}>{t('property:tm.save', { n: rows.length })}</button>
        </div>
      </div>
      {suggestOpen && (
        <SuggestionPanel
          sourceColumns={sourceColumns}
          targetColumns={targetColumns}
          sourceRows={sourceRows}
          onApply={applySuggestions}
          onClose={() => setSuggestOpen(false)}
        />
      )}
    </div>
  );
};

export default TransformMappingModal;
